// M005-F0052 (M005-D005 §4.3/§5) — Bookings Hub permission predicate.
//
// The hub's tab set and its nav entry are gated on the actor's REAL
// organisation-capability keys, read from
// `GET /api/organisations/:id/my-permissions` via
// `getMyEffectiveOrganisationPermissions` (src/services/api.js) — never from
// `organisation_users.role` / `users.role` name-matching (M004-D001's
// "two separate role fields" invariant; a `role === 'admin'` check here
// would be the same bug class the design doc §Related Documents calls out).
//
// Fail closed: anything short of an explicit `true` for at least one of the
// three keys (missing map, tri-state 'partial', read failure — the helper
// already resolves errors to `{}`) means "no hub chrome".
export const BOOKINGS_HUB_PERMISSION_KEYS = [
  'bookings_manage_all',
  'pricing_plans_view',
  'pricing_booking_reprice',
  // M004-F0634 §2 — the Billing tab's key joins the HUB-level set so a
  // holder with no other hub key still gets the hub's nav entry (Layout.js
  // reads hasBookingsHubPermission per org; no second my-permissions
  // fan-out). TAB visibility stays narrower: hasBookingsHubStudioPermission
  // (the original three keys) gates Studio/Calendar so a ledger-only holder
  // sees the hub and ONLY the Billing tab, never the booking-management
  // chrome.
  'ledger_dispatch_approve',
  // M004-F0717 (design 15.7): a billing.view-only member gets the nav entry and the transaction Calendar tab.
  'billing_view',
];

export const hasBookingsHubPermission = (permissions) =>
  BOOKINGS_HUB_PERMISSION_KEYS.some((key) => permissions?.[key] === true);

// M004-F0634 §2 — Studio/Calendar keep the ORIGINAL hub key set: a
// ledger_dispatch_approve-only holder must get the nav entry and the
// Billing tab, and nothing else (WEB-BILLPIPE-004's view-only persona sees
// the hub without booking-management tabs).
export const BOOKINGS_HUB_STUDIO_KEYS = [
  'bookings_manage_all',
  'pricing_plans_view',
  'pricing_booking_reprice',
];

export const hasBookingsHubStudioPermission = (permissions) =>
  BOOKINGS_HUB_STUDIO_KEYS.some((key) => permissions?.[key] === true);

// M004-F0634 §2/§5 — the Billing tab renders iff the selected org's
// my-permissions read resolves ledger_dispatch_approve === true. Same
// fail-closed convention: tri-state 'partial', missing map, or a read
// failure (resolved {}) all hide the tab.
//
// This predicate's meaning is UNCHANGED by the 2026-09-24 flag below — it
// stays a pure permission read so bookingsHubPermissions.test.js's fixed
// cases keep meaning exactly what they always meant. The flag is a SEPARATE
// gate, combined with this one only at the call site in BookingsHub.js.
export const hasBillingTabPermission = (permissions) =>
  permissions?.ledger_dispatch_approve === true;

// M004-F0634 §10 (2026-09-24 incident amendment) — the Billing tab's
// backend does not exist on Core main/production: WebUI !405 (this tab)
// merged and deployed, but its Core half (Core !695 — the
// `GET /v1/orgs/:orgId/billing-pipeline[...]` routes) is unmerged and last
// in the Core queue. A `ledger_dispatch_approve` holder hitting
// /app/bookings?tab=billing on production got Core's v1 router catch-all
// ("Endpoint not found", src/routes/v1/index.ts) instead of the tab.
//
// Hidden behind this build-time flag, DEFAULT OFF, until Core !695 is
// merged AND deployed to production — at which point this flag flips to
// '1' in the production build env (never a code change to this predicate)
// and is removed once Core !695 has been live for one release. Never a
// client-side probe of the 404 route: that would be racing the same
// endpoint that is missing, once per page load, for every holder.
export const isBillingPipelineBackendAvailable = () => (
  process.env.REACT_APP_BILLING_PIPELINE_ENABLED === '1'
);

// M005-F0064 (M005-D005 §9e, amendment 2026-09-08) — the booking drawer's
// price-plan selector slot gate. The owner's "for the admin only" is this
// predicate, never `role === 'admin'` name-matching: the slot renders only
// when the SELECTED organisation's my-permissions projection grants a
// pricing key. Same fail-closed convention as the hub gate above — anything
// short of an explicit `true` (missing map, tri-state 'partial', read
// failure resolved to {}) hides the slot. The pricing keys here are the two
// that exist today; §9e names the wider #1385 key set for when those land.
//
// The gate lives HERE, in the drawer, as the single enforcement point: the
// slot content itself (the plan selector, WEB-BKHUB-018) is supplied by the
// stacking lane and must never re-check permissions — a second gate here
// would be two chances to disagree.
export const PLAN_SELECTOR_PERMISSION_KEYS = [
  'pricing_plans_view',
  'pricing_booking_reprice',
];

export const hasPlanSelectorPermission = (permissions) =>
  PLAN_SELECTOR_PERMISSION_KEYS.some((key) => permissions?.[key] === true);
